import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, ArrowUp, Check, ClipboardCheck, Loader2, RotateCw, Settings2, SquarePen } from "lucide-react";
import { api } from "../api";
import { local } from "../storage";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import ChatMessage, { AnalystMark } from "../components/ChatMessage";
import { ErrorState, PageHeader } from "../components/Ui";
import { readStream } from "../ads-ai";
import { JARVIS_STARTERS, providerLabel, type JarvisAction, type JarvisEvent, type JarvisMessage, type JarvisSettings } from "../jarvis";

type Session = { id: number; title: string | null; updated_at: string; running?: boolean };
const LAST_KEY = "nl-aos-jarvis-session";
const POLL_MS = 6000;

/** Jarvis: the agency-wide assistant. Reads any staff page's data as you, and adds tasks when asked. */
export default function Jarvis() {
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const sessionId = Number(params.get("session")) || null;
  const sessions = useApi<Session[]>("/api/jarvis/sessions");
  const settings = useApi<JarvisSettings>("/api/jarvis/settings");
  const [messages, setMessages] = useState<JarvisMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(params.get("go") ? "" : params.get("ask") || "");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState<{ steps: string[]; actions: JarvisAction[] }>({ steps: [], actions: [] });
  const [failure, setFailure] = useState<{ text: string; message: string } | null>(null);
  const [waiting, setWaiting] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const followAnswer = useRef(true);
  const skipLoad = useRef(false);
  const alive = useRef(true);
  const sending = useRef(false);
  const reopened = useRef(false);
  const asked = useRef(false);
  const stream = useRef<AbortController | null>(null);
  const loader = useRef<AbortController | null>(null);
  const poll = useRef<number | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; stream.current?.abort(); loader.current?.abort(); if (poll.current) window.clearTimeout(poll.current); };
  }, []);

  const load = useCallback(async (id: number, quiet = false) => {
    loader.current?.abort();
    if (poll.current) { window.clearTimeout(poll.current); poll.current = null; }
    const controller = new AbortController(); loader.current = controller;
    if (!quiet) { setLoading(true); setLoadError(""); }
    try {
      const r = await api<{ session?: { running?: boolean }; messages: JarvisMessage[] }>(`/api/jarvis/sessions/${id}`, { signal: controller.signal });
      if (!alive.current || controller.signal.aborted) return;
      setMessages(r.messages);
      const last = r.messages[r.messages.length - 1];
      const unanswered = last?.role === "user"; const running = Boolean(r.session?.running);
      setWaiting(unanswered && running);
      const question = [...r.messages].reverse().find((m) => m.role === "user");
      setFailure(unanswered && !running ? { text: last.content, message: "This answer did not finish. Ask again to get it." }
        : last?.failed && question ? { text: question.content, message: last.content } : null);
      if (unanswered && running) poll.current = window.setTimeout(() => { if (alive.current) void load(id, true); }, POLL_MS);
    } catch (e) {
      if (!alive.current || controller.signal.aborted) return;
      if (!quiet) setLoadError(e instanceof Error ? e.message : "The conversation could not load.");
    } finally {
      if (loader.current === controller) { loader.current = null; if (alive.current && !quiet) setLoading(false); }
    }
  }, []);

  useEffect(() => {
    followAnswer.current = true;
    if (sessionId) local.set(LAST_KEY, String(sessionId));
    if (skipLoad.current) { skipLoad.current = false; return; }
    stream.current?.abort(); stream.current = null; sending.current = false;
    setBusy(false); setWaiting(false); setLive({ steps: [], actions: [] }); setFailure(null); setLoadError("");
    if (sessionId) void load(sessionId); else { setMessages([]); setLoading(false); }
  }, [sessionId, load]);

  // Coming back reopens the conversation you had open, unless the overview sent a new question.
  useEffect(() => {
    if (reopened.current || !sessions.data) return;
    reopened.current = true;
    if (sessionId || params.get("ask")) return;
    const stored = Number(local.get(LAST_KEY)) || null;
    const pick = sessions.data.find((x) => x.id === stored) || sessions.data.find((x) => x.running);
    if (stored && !pick) local.remove(LAST_KEY);
    if (!pick) return;
    const next = new URLSearchParams(params); next.set("session", String(pick.id)); setParams(next, { replace: true });
  }, [sessions.data, sessionId, params, setParams]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const welcome = !sessionId && !messages.length && !busy;
    if (welcome || followAnswer.current) el.scrollTo({ top: welcome ? 0 : el.scrollHeight, behavior: "auto" });
  }, [sessionId, messages.length, live.steps.length, live.actions.length, busy]);

  async function send(text: string) {
    const t = text.trim();
    if (!t || sending.current || waiting || loader.current || loadError) return;
    sending.current = true; followAnswer.current = true;
    const controller = new AbortController(); stream.current = controller;
    setBusy(true); setFailure(null); setDraft(""); setLive({ steps: [], actions: [] });
    setMessages((list) => [...list, { id: `u${Date.now()}`, role: "user", content: t }]);
    let current = sessionId;
    try {
      await readStream<JarvisEvent>("/api/jarvis/chat", { session_id: current, message: t, page: location.pathname }, (ev) => {
        if (!alive.current || controller.signal.aborted || stream.current !== controller) return;
        if (ev.type === "session" && ev.id !== current) {
          current = ev.id; skipLoad.current = true;
          const next = new URLSearchParams(params); next.set("session", String(ev.id)); next.delete("ask"); next.delete("go");
          setParams(next, { replace: true });
        }
        if (ev.type === "step") setLive((l) => ({ ...l, steps: [...l.steps, ev.label] }));
        if (ev.type === "action") setLive((l) => ({ ...l, actions: [...l.actions, ev.action] }));
        if (ev.type === "done") setMessages((list) => [...list, ev.message]);
        if (ev.type === "error") throw new Error(ev.error);
      }, controller.signal, "Jarvis");
      if (alive.current && !controller.signal.aborted) sessions.reload();
    } catch (e) {
      if (alive.current && !controller.signal.aborted) {
        setFailure({ text: t, message: e instanceof Error ? e.message : "Jarvis could not answer." });
        if (current) void load(current, true);
      }
    } finally {
      if (stream.current === controller) {
        stream.current = null; sending.current = false;
        if (alive.current) { setBusy(false); setLive({ steps: [], actions: [] }); }
      }
    }
  }

  // A question from the Agency overview box is asked as the page opens, in a new conversation.
  useEffect(() => {
    const ask = params.get("ask");
    if (asked.current || !ask || !params.get("go") || sessionId) return;
    asked.current = true;
    void send(ask);
  });

  function newChat() {
    if (sending.current) return;
    local.remove(LAST_KEY);
    const next = new URLSearchParams(params); next.delete("session"); next.delete("ask"); next.delete("go");
    setParams(next); setMessages([]); setFailure(null); setDraft("");
  }

  const choice = settings.data?.choices.find((c) => c.id === settings.data?.provider);
  const lastAssistant = messages.map((m) => m.role).lastIndexOf("assistant");
  const empty = !sessionId && !messages.length && !busy;
  // What Jarvis reads is in the empty chat; the subtitle speaks only when the AI it runs on is not connected.
  const notConnected = choice && !choice.connected ? `Runs on ${choice.label}, which is not connected.` : undefined;

  return (
    <div className="ads-ai-page jarvis-page">
      <PageHeader
        title="Jarvis"
        description={notConnected}
        actions={<>
          {(sessions.data?.length || 0) > 0 && (
            <select className="ads-ai-sessions" aria-label="Past conversations" disabled={busy} value={sessionId ?? ""} onChange={(e) => {
              const next = new URLSearchParams(params); if (e.target.value) next.set("session", e.target.value); else next.delete("session"); setParams(next);
            }}>
              <option value="">Past conversations</option>
              {sessions.data!.map((s) => <option key={s.id} value={s.id}>{s.title || `Conversation ${s.id}`}</option>)}
            </select>
          )}
          {admin && <Link className="button secondary" to="/settings#jarvis"><Settings2 aria-hidden="true" />AI settings</Link>}
          <button type="button" className="button secondary" onClick={newChat} disabled={busy}><SquarePen aria-hidden="true" />New chat</button>
        </>}
      />
      <div className="analyst-chat ads-ai-chat">
        <section className="ac-main" aria-label="Jarvis conversation">
          <div className="ac-scroll" ref={scroller} onScroll={(e) => {
            const el = e.currentTarget;
            followAnswer.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
          }}>
            {loading ? (
              <div className="ac-thread ac-thread-skeleton" aria-label="Loading conversation"><span className="ac-sk-user" /><span className="ac-sk-line" /><span className="ac-sk-line short" /></div>
            ) : loadError ? (
              <div className="ac-thread"><ErrorState message={loadError} onRetry={() => sessionId && void load(sessionId)} /></div>
            ) : empty ? (
              <div className="ac-welcome">
                <AnalystMark />
                <h2>Ask Jarvis about the agency</h2>
                <p>Jarvis reads the same pages you can: clients, ads, health, calls, the work queue, approvals, CRM and the knowledge base. It can add tasks to the work queue. It does not change ads, budgets or settings.</p>
                <div className="ac-starters">
                  {JARVIS_STARTERS.map((s) => (
                    <button key={s} type="button" className="ac-starter" onClick={() => void send(s)}><span>{s}</span><ArrowRight aria-hidden="true" /></button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="ac-thread" role="log" aria-label="Conversation with Jarvis" aria-busy={busy}>
                {/* The last failed reply is shown once, in the No answer box with Retry. */}
                {messages.filter((m, i) => !(m.failed && i === messages.length - 1 && failure)).map((m, i) => (
                  <ChatMessage key={m.id} message={{ id: m.id, role: m.role, content_text: m.content, created_at: m.created_at }} last={i === lastAssistant && !busy}>
                    {m.role === "assistant" && (m.actions || []).map((a) => <ActionCard key={`${a.type}${a.id}`} action={a} />)}
                    {m.role === "assistant" && !m.failed && <Steps steps={m.steps || []} by={providerLabel(m.provider, m.model)} />}
                  </ChatMessage>
                ))}
                {waiting && !busy && (
                  <div className="ac-msg ac-msg-assistant ads-ai-working" aria-live="polite">
                    <AnalystMark />
                    <div className="ac-reply"><ul className="ads-ai-live"><li className="now"><Loader2 className="spin" aria-hidden="true" />Still working on this answer. It shows up here when it is done.</li></ul></div>
                  </div>
                )}
                {busy && (
                  <div className="ac-msg ac-msg-assistant ads-ai-working" aria-live="polite">
                    <AnalystMark />
                    <div className="ac-reply">
                      <ul className="ads-ai-live">
                        {live.steps.map((s, i) => <li key={i} className={i === live.steps.length - 1 ? "now" : ""}>{i === live.steps.length - 1 ? <Loader2 className="spin" aria-hidden="true" /> : <Check aria-hidden="true" />}{s}</li>)}
                        {!live.steps.length && <li className="now"><Loader2 className="spin" aria-hidden="true" />Thinking</li>}
                      </ul>
                      {live.actions.map((a) => <ActionCard key={`${a.type}${a.id}`} action={a} />)}
                    </div>
                  </div>
                )}
                {failure && (
                  <div className="ac-error" role="alert">
                    <AlertCircle aria-hidden="true" />
                    <p><strong>No answer.</strong> {failure.message}</p>
                    <button type="button" className="ac-retry" onClick={() => void send(failure.text)}><RotateCw aria-hidden="true" />Retry</button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="ac-composer-wrap">
            <form className="ac-composer" onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
              <label htmlFor="jarvis-message" className="sr-only">Message Jarvis</label>
              <textarea id="jarvis-message" className="ac-input" rows={1} value={draft} maxLength={4000} disabled={busy || waiting || loading || !!loadError}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(draft); } }}
                placeholder={busy || waiting ? "Jarvis is working…" : "Ask Jarvis anything about the agency…"} />
              <button type="submit" className="ac-send" disabled={busy || waiting || loading || !!loadError || !draft.trim()} aria-label="Send message"><ArrowUp aria-hidden="true" /></button>
            </form>
            <p className="ac-hint">Enter to send, Shift+Enter for a new line.</p>
          </div>
        </section>
      </div>
    </div>
  );
}

/** What Jarvis read for this answer and which AI wrote it, folded away. */
function Steps({ steps, by }: { steps: string[]; by: string }) {
  if (!steps.length && !by) return null;
  return (
    <details className="ads-ai-steps">
      <summary>{steps.length ? `What Jarvis read${by ? `, answered by ${by}` : ""}` : `Answered by ${by}`}</summary>
      {steps.length > 0 && <ul>{steps.map((s, i) => <li key={i}>{s}</li>)}</ul>}
    </details>
  );
}

/** Something Jarvis did, with a link to where it is. */
function ActionCard({ action }: { action: JarvisAction }) {
  return (
    <div className="ads-ai-proposal is-applied">
      <div className="ads-ai-proposal-main">
        <span className="ads-ai-proposal-kind"><ClipboardCheck aria-hidden="true" /> Task added</span>
        <strong>{action.title}</strong>
        {action.due_date && <span className="muted">Due {new Date(`${action.due_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
      </div>
      <div className="ads-ai-proposal-actions"><Link className="text-button" to={action.to}>Open work queue</Link></div>
    </div>
  );
}
